// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@steiner/Overrides/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import validateCheckoutRecaptcha, {
    ValidateCheckoutRecaptchaApiParameter,
} from "@steiner/App/Handlers/Checkout/ValidateCheckoutRecaptcha";
import { CheckoutRecaptchaValidateResponse } from "@steiner/App/Services/AddCheckoutRecaptcha";
import React, { useCallback, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { ResolveThunks, connect, shallowEqual } from "react-redux";

interface OwnProps {
    onVerified?: () => void;
}

const mapDispatchToProps = {
    validateCheckoutRecaptcha,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

type ValidateCheckoutRecaptchaParameter = ValidateCheckoutRecaptchaApiParameter &
    HasOnSuccess<CheckoutRecaptchaValidateResponse> &
    HasOnError<string>;

export interface CheckoutRecaptchaStyles {
    wrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    header?: InjectableCss;
    headerTitle?: TypographyPresentationProps;
    recaptchaWrapper?: InjectableCss;
    recaptchaField?: InjectableCss;
    continueColumn?: InjectableCss;
    continueButton?: ButtonPresentationProps;
}

export const checkoutRecaptchaStyles: CheckoutRecaptchaStyles = {};

const styles = checkoutRecaptchaStyles;

const CheckoutRecaptcha: React.FC<Props> = ({ onVerified, validateCheckoutRecaptcha }) => {
    const { cartUrl } = useAppSelector(
        state => ({
            cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        }),
        shallowEqual,
    );
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [hasRecaptchaToken, setHasRecaptchaToken] = useState(false);

    const onContinue = useCallback(() => {
        const token = recaptchaRef.current?.getValue() || "";
        if (!token) {
            return;
        }
        setSubmitting(true);
        const parameter: ValidateCheckoutRecaptchaParameter = {
            token,
            onSuccess: () => {
                setSubmitting(false);
                onVerified?.();
            },
            onError: () => {
                setSubmitting(false);
                recaptchaRef.current?.reset();
                setHasRecaptchaToken(false);
            },
        };
        validateCheckoutRecaptcha(parameter);
    }, [onVerified, validateCheckoutRecaptcha]);

    return (
        <StyledWrapper {...styles.wrapper}>
            {createWidgetElement("Basic/PageTitle", {
                fields: {
                    overrideTitle: translate("Checkout"),
                    showGoBack: true,
                    goBackTitle: translate("Return to Cart"),
                    goBackLink: cartUrl,
                },
            })}
            <StyledWrapper {...styles.cardWrapper}>
                <StyledWrapper {...styles.header}>
                    <Typography {...styles.headerTitle}>{translate("Before We Continue")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.recaptchaWrapper}>
                    <StyledWrapper {...styles.recaptchaField}>
                        <ReCaptcha
                            recaptchaRef={recaptchaRef}
                            location="CheckoutShipping"
                            onTokenChange={token => setHasRecaptchaToken(!!token)}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.continueColumn}>
                        <Button
                            {...styles.continueButton}
                            onClick={onContinue}
                            disabled={submitting || !hasRecaptchaToken}
                            data-test-selector="checkoutRecaptcha_continue"
                        >
                            {translate("Continue to Checkout")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(null, mapDispatchToProps)(CheckoutRecaptcha);
